import _ from 'lodash';
import { SportType } from 'modules/core/domain/enums/sport-type';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { IOrganizerCard } from 'modules/organizers/domain/interfaces/organizer-card';
import OrganizerCard from 'modules/organizers/ui/components/organizer-row';
import { TournamentListItem } from 'modules/tournaments/domain/interfaces/tournament-list-item';
import { useTournamentsListV2 } from 'modules/tournaments/ui/hooks/use-tournaments-list-v2';
import UDPagination from 'modules/ud-ui/components/pagination';
import UDPerPage from 'modules/ud-ui/components/per-page/per-page';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { SingleValue } from 'react-select';
import { SyncLoader } from 'react-spinners';
import UDInput from 'ud-form/components/input/component';
import { SelectOption, UDSelect } from 'ud-form/components/select/component';
import UDHeaderTitle from 'ud-ui/components/header-title';
import ImgNotFound from 'ud-ui/components/images/not-found.png';
import Label from 'ud-ui/components/label';
import UDText from 'ud-ui/components/text';
import { useMainLayout } from 'ud-ui/layout/main/context';
import { FiltersBlock, MainBlock, RowsContainer } from './styles';

function mapTournamentToOrganizerCard(item: TournamentListItem): IOrganizerCard {
  return {
    name: item.name,
    sportType: item.sportType,
    phone: item.phone,
    logo: item.logoUrl,
    createAt: item.createdAt,
    user: item.adminUser,
    address: item.address,
  };
}

function OrganizersList() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { setHeaderContent } = useMainLayout();
  const { items, pages, isLoading, isError, changeFilter, changePerPage, filters } = useTournamentsListV2();

  useEffect(() => {
    setHeaderContent(<UDHeaderTitle>Организаторы</UDHeaderTitle>);
    return () => setHeaderContent(null);
  }, [setHeaderContent]);

  const sportTypeOptions = useMemo<SelectOption<string>[]>(() => {
    const types = _.keys(SportType).map((key) => ({
      value: key,
      label: _.capitalize(t(`sportType.${key}`)),
    }));
    return [{ value: '', label: 'Все' }, ...types];
  }, [t]);

  const sortOptions = useMemo<SelectOption<string>[]>(
    () => [
      { value: 'DESC', label: 'Новые' },
      { value: 'ASC', label: 'Старые' },
    ],
    [],
  );

  const selectedSportType = sportTypeOptions.find((o) => o.value === (filters.sportType ?? '')) ?? sportTypeOptions[0];
  const selectedSort = sortOptions.find((o) => o.value === (filters.sortByCreatedAt ?? 'DESC')) ?? sortOptions[0];

  const handleSportTypeChange = (v: SingleValue<SelectOption<string>>) => {
    changeFilter({ sportType: v?.value || undefined, page: 1 });
  };

  const handleSortChange = (v: SingleValue<SelectOption<string>>) => {
    changeFilter({ sortByCreatedAt: v?.value ?? 'DESC', page: 1 });
  };

  return (
    <MainBlock className={'flex-1'}>
      <FiltersBlock className={'mb-6'}>
        <Label text="Поиск по названию:">
          <UDInput
            placeholder="Название"
            value={filters.search ?? ''}
            onChange={(e) => changeFilter({ search: e.target.value || undefined, page: 1 })}
            wrapperProps={{ style: { width: 260 } }}
          />
        </Label>

        <Label text="Телефон:">
          <UDInput
            placeholder="79139332245"
            value={filters.phone ?? ''}
            onChange={(e) => changeFilter({ phone: e.target.value || undefined, page: 1 })}
            wrapperProps={{ style: { width: 200 } }}
          />
        </Label>

        <Label text="Тип спорта:" style={{ width: 180 }}>
          <UDSelect isMulti={false} options={sportTypeOptions} value={selectedSportType} onChange={handleSportTypeChange} />
        </Label>

        <Label text="Сортировать по дате:" style={{ width: 180 }}>
          <UDSelect isMulti={false} options={sortOptions} value={selectedSort} onChange={handleSortChange} />
        </Label>

        <UDPerPage style={{ marginLeft: 'auto' }} value={filters.perPage} onChange={changePerPage} />
      </FiltersBlock>

      <RowsContainer>
        {/* {isLoading && ( */}
        {isLoading && (
          <div className="flex-center w-full px-6" style={{ height: '100%', minHeight: 'calc(100vh - 241px)' }}>
            <SyncLoader />
          </div>
        )}
        {!isLoading && isError && (
          <div className="flex-center w-full px-6" style={{ height: '100%', flexDirection: 'column' }}>
            <img style={{ width: 130, height: 130 }} alt="error" src={ImgNotFound} />
            <UDText type="callout" className="text-surfaceRomanSilver30">
              Ошибка загрузки данных. Попробуйте изменить фильтры или обновить страницу.
            </UDText>
          </div>
        )}
        {!isLoading && !isError && items.length === 0 && (
          <div className="flex-center w-full px-6" style={{ height: '100%', flexDirection: 'column' }}>
            <img style={{ width: 130, height: 130 }} alt="not found" src={ImgNotFound} />
            <UDText type="callout" className="text-surfaceRomanSilver30">
              По такому запросу ничего не найдено
            </UDText>
          </div>
        )}
        {!isLoading &&
          !isError &&
          items.map((item) => (
            <OrganizerCard
              key={item.id}
              onClick={() => navigate(Pages.ORGANIZERS.builders.view(item.id))}
              {...mapTournamentToOrganizerCard(item)}
            />
          ))}
      </RowsContainer>

      {pages > 1 && (
        <UDPagination
          className="my-4 px-6"
          pages={pages}
          currentPage={filters.page}
          onPageChange={(p) => changeFilter({ page: p })}
          disabled={isLoading}
        />
      )}
    </MainBlock>
  );
}

export default React.memo(OrganizersList);
